import { Info } from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomstructureset';
import { environmentState } from '~/state/atoms/engineAtoms';
import { selectedROIState } from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    keepEveryNthSliceState,
    keepEveryNthSliceValueState,
    minSliceInterval,
} from '~/state/atoms/toolsAtoms';

export default function KeepEveryNthSliceTool() {
    const [keepEveryNthSlice, setKeepEveryNthSlice] = useRecoilState(
        keepEveryNthSliceState
    );
    const [keepEveryNthSliceValue, setKeepEveryNthSliceValue] = useRecoilState(
        keepEveryNthSliceValueState
    );
    const selectedROI = useRecoilValue(selectedROIState);
    const structureSet = useRecoilValue(currentStructureSetState);
    const environment = useRecoilValue(environmentState);
    const selectedStructure = environment?.structureSet?.getRoi(selectedROI);
    const selectedStructureName = selectedStructure?.name;
    const structureApprovalStatus =
        selectedStructure?.dicomParent.approvalStatus;

    const handleKeepEveryNthSliceValue = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const parsedValue = parseInt(e.target.value, 10);
        if (isNaN(parsedValue) || parsedValue < minSliceInterval) {
            return;
        }
        setKeepEveryNthSliceValue(parsedValue);
    };

    const applyKeepEveryNthSliceOperation = () => {
        if (environment && selectedROI !== -1 && keepEveryNthSliceValue > 1) {
            environment.keepEveryNthSlice(selectedROI, keepEveryNthSliceValue);
        }
    };

    const applyClearAllSlicesOperation = () => {
        if (environment && selectedStructureName) {
            environment.clearAllContours(selectedStructureName);
        }
    };

    const handleClearAllSlices = () => {
        if (structureApprovalStatus === DICOMApprovalStatus.APPROVED) {
            enqueueSnackbar(
                'Clearing slices is not allowed for this ROI as it has already been reviewed.',
                { variant: 'warning' }
            );
            return;
        }
        if (keepEveryNthSlice) {
            applyKeepEveryNthSliceOperation();
        } else {
            applyClearAllSlicesOperation();
        }
    };

    const getKeepNthSliceTextValue = () => {
        const tens = keepEveryNthSliceValue % 100;
        const units = keepEveryNthSliceValue % 10;
        let suffix;

        if (tens >= 11 && tens <= 13) {
            suffix = 'th';
        } else if (units === 1) {
            suffix = 'st';
        } else if (units === 2) {
            suffix = 'nd';
        } else if (units === 3) {
            suffix = 'rd';
        } else {
            suffix = 'th';
        }

        return `${keepEveryNthSliceValue}${suffix}`;
    };

    const hasStructures = !!structureSet?.roiArray?.length;
    const applyOperationDisabled =
        !hasStructures || selectedROI === -1 || structureSet?.rois.size === 0;

    return (
        <React.Fragment>
            {!applyOperationDisabled ? (
                <Stack>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent={
                            keepEveryNthSlice ? 'space-between' : 'left'
                        }
                        mb={2}
                    >
                        <Box display="flex" alignItems="center">
                            <Checkbox
                                size="small"
                                color="primary"
                                checked={keepEveryNthSlice}
                                onChange={() =>
                                    setKeepEveryNthSlice(!keepEveryNthSlice)
                                }
                            />
                            <Typography variant="body2" mr={1}>
                                <FormattedMessage id="clearToolKeepSlice" />
                            </Typography>
                            <Tooltip
                                title={
                                    <FormattedMessage id="clearToolSliceRange" />
                                }
                            >
                                <Info fontSize="small" />
                            </Tooltip>
                        </Box>
                        <Box sx={{ transition: 'opacity 0.3s ease' }}>
                            <TextField
                                fullWidth
                                label={<FormattedMessage id="nthSlice" />}
                                value={keepEveryNthSliceValue}
                                onChange={handleKeepEveryNthSliceValue}
                                sx={{
                                    fontSize: 12,
                                    maxWidth: 100,
                                    visibility: keepEveryNthSlice
                                        ? 'visible'
                                        : 'hidden',
                                    opacity: keepEveryNthSlice ? 1 : 0,
                                }}
                                inputProps={{
                                    min: 2,
                                    step: 1,
                                    type: 'number',
                                }}
                                size="small"
                            />
                        </Box>
                    </Box>
                    <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        onClick={handleClearAllSlices}
                        disabled={applyOperationDisabled}
                    >
                        {keepEveryNthSlice ? (
                            <FormattedMessage
                                id="clearToolAllSlicesKeepEveryNthSlice"
                                values={{
                                    nthSlice: getKeepNthSliceTextValue(),
                                }}
                            />
                        ) : (
                            <FormattedMessage id="clearToolAllSlices" />
                        )}
                    </Button>
                </Stack>
            ) : null}
        </React.Fragment>
    );
}
